import { useEffect, useState } from "react";
import { AlertTriangle, Check, Eye, EyeOff, Plus, RotateCcw } from "lucide-react";
import { Button, Callout, CopyButton, Input, Modal } from "./ui";
import { ApiError, gauntlet } from "../lib/api";
import { cx } from "../lib/format";

interface CreateResult {
  mnemonic: string;
  wallet: { id: number; name: string };
}

/**
 * Wallet creation, as a three-step flow.
 *
 * The middle step exists entirely to slow the user down. The recovery phrase is
 * displayed exactly once, and if it is not written down at that moment the
 * funds are unrecoverable — so the flow blurs it until deliberately revealed,
 * and will not advance until the user has ticked an explicit acknowledgement.
 * Making this frictionless would be a disservice.
 */
export function CreateWalletDialog({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (input: {
    name: string;
    password: string;
    words: number;
    passphrase?: string;
    mnemonic?: string;
  }) => Promise<CreateResult>;
}) {
  const [mode, setMode] = useState<"create" | "restore">("create");
  const [step, setStep] = useState<"form" | "phrase" | "done">("form");
  const [name, setName] = useState("My Wallet");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [words, setWords] = useState(24);
  const [passphrase, setPassphrase] = useState("");
  const [usePassphrase, setUsePassphrase] = useState(false);

  const [mnemonic, setMnemonic] = useState("");
  /** Restore mode: the words the user is typing in, one per box. */
  const [entry, setEntry] = useState<string[]>(() => Array(24).fill(""));
  const [entryLength, setEntryLength] = useState(24);
  const [focused, setFocused] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [checked, setChecked] = useState<{
    valid: boolean;
    wordCount: number;
    problems: string[];
  } | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const typed = entry.slice(0, entryLength).map((w) => w.trim().toLowerCase());
  const allFilled = typed.every(Boolean);

  /* BIP-39 autocomplete. Only the 2048-word list is consulted and it lives on
     the server, so this leaks nothing an attacker could not already guess — but
     it catches the transcription slips that otherwise cost people their funds. */
  useEffect(() => {
    if (mode !== "restore" || focused === null) {
      setSuggestions([]);
      return;
    }
    const prefix = entry[focused]?.trim().toLowerCase() ?? "";
    if (prefix.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    void gauntlet
      .suggestWords(prefix)
      .then((r) => {
        if (!cancelled) setSuggestions(r.words.slice(0, 6));
      })
      .catch(() => setSuggestions([]));
    return () => {
      cancelled = true;
    };
  }, [mode, focused, entry]);

  /* Checksum check as soon as the grid is full. A phrase can be 24 real BIP-39
     words and still be invalid; saying so here beats a restore that silently
     opens an empty wallet. */
  useEffect(() => {
    if (mode !== "restore" || !allFilled) {
      setChecked(null);
      return;
    }
    let cancelled = false;
    void gauntlet
      .validateMnemonic(typed.join(" "))
      .then((r) => {
        if (!cancelled) setChecked(r);
      })
      .catch(() => setChecked(null));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, allFilled, typed.join(" ")]);

  /** Accept a whole phrase pasted into any one box. */
  const handlePaste = (index: number, text: string): boolean => {
    const parts = text.trim().toLowerCase().split(/\s+/);
    if (parts.length < 2) return false;
    const next = [...entry];
    if (parts.length === 12 || parts.length === 24) {
      setEntryLength(parts.length);
      for (let i = 0; i < 24; i++) next[i] = parts[i] ?? "";
    } else {
      parts.forEach((word, offset) => {
        if (index + offset < 24) next[index + offset] = word;
      });
    }
    setEntry(next);
    return true;
  };

  const reset = (): void => {
    setMode("create");
    setStep("form");
    setEntry(Array(24).fill(""));
    setEntryLength(24);
    setChecked(null);
    setSuggestions([]);
    setName("My Wallet");
    setPassword("");
    setConfirm("");
    setPassphrase("");
    setUsePassphrase(false);
    setMnemonic("");
    setRevealed(false);
    setAcknowledged(false);
    setError(null);
  };

  const close = (): void => {
    reset();
    onClose();
  };

  const submit = async (): Promise<void> => {
    setError(null);
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const result = await onCreate({
        name,
        password,
        words,
        ...(usePassphrase && passphrase ? { passphrase } : {}),
        ...(mode === "restore" ? { mnemonic: typed.join(" ") } : {}),
      });
      // A restored wallet's phrase is already written down somewhere; showing
      // it back would be theatre, so skip straight to the confirmation.
      if (mode === "restore") {
        setStep("done");
      } else {
        setMnemonic(result.mnemonic);
        setStep("phrase");
      }
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not create the wallet.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={step === "phrase" ? () => undefined : close}
      title={
        step === "form"
          ? mode === "create"
            ? "Create a wallet"
            : "Restore a wallet"
          : step === "phrase"
            ? "Your recovery phrase"
            : "Wallet created"
      }
      width={step === "phrase" || mode === "restore" ? "max-w-lg" : "max-w-md"}
    >
      {step === "form" && (
        <div className="space-y-4">
          {/* Someone reinstalling needs this on the first screen, not buried in
              a settings page they have to already know about. */}
          <div className="flex gap-2">
            {(
              [
                ["create", "Create new", Plus],
                ["restore", "I have a phrase", RotateCcw],
              ] as const
            ).map(([id, label, Icon]) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                className={cx(
                  "flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors",
                  mode === id
                    ? "border-brass/50 bg-brass-wash text-brass"
                    : "border-hairline text-ink-dim hover:border-hairline-bright",
                )}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
          </div>

          <Input
            label="Wallet name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />

          {mode === "restore" ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="block text-[13px] font-medium text-ink-dim">
                  Your recovery phrase
                </span>
                <div className="flex gap-1">
                  {[12, 24].map((count) => (
                    <button
                      key={count}
                      type="button"
                      onClick={() => setEntryLength(count)}
                      className={cx(
                        "rounded-md border px-2 py-0.5 text-xs transition-colors",
                        entryLength === count
                          ? "border-brass/50 bg-brass-wash text-brass"
                          : "border-hairline text-ink-dim",
                      )}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>

              <ol className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {Array.from({ length: entryLength }, (_, i) => (
                  <li key={i} className="relative flex items-center gap-1">
                    <span className="num w-5 shrink-0 text-right text-[11px] text-ink-faint">
                      {i + 1}
                    </span>
                    <input
                      value={entry[i] ?? ""}
                      onChange={(e) => {
                        const next = [...entry];
                        next[i] = e.target.value.replace(/[^a-zA-Z]/g, "").toLowerCase();
                        setEntry(next);
                      }}
                      onFocus={() => setFocused(i)}
                      onBlur={() => window.setTimeout(() => setFocused(null), 150)}
                      onPaste={(e) => {
                        if (handlePaste(i, e.clipboardData.getData("text"))) {
                          e.preventDefault();
                        }
                      }}
                      autoComplete="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      className="h-8 w-full min-w-0 rounded border border-hairline bg-abyss px-2 font-mono text-xs text-ink outline-none focus:border-brass/50"
                    />
                    {focused === i && suggestions.length > 0 && (
                      <ul className="absolute left-6 top-9 z-20 w-32 overflow-hidden rounded-md border border-hairline bg-raised shadow-lg">
                        {suggestions.map((word) => (
                          <li key={word}>
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                const next = [...entry];
                                next[i] = word;
                                setEntry(next);
                              }}
                              className="block w-full px-2 py-1 text-left font-mono text-xs hover:bg-brass-wash hover:text-brass"
                            >
                              {word}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>

              <p className="text-xs text-ink-faint">
                Paste the whole phrase into any box and it will fill the grid.
              </p>

              {checked && !checked.valid && (
                <Callout tone="danger" title="That phrase does not check out">
                  {checked.problems.join(" ")}
                </Callout>
              )}
              {checked?.valid && (
                <Callout tone="success">
                  Valid {checked.wordCount}-word phrase. The checksum matches.
                </Callout>
              )}
            </div>
          ) : (
          <div className="space-y-1.5">
            <span className="block text-[13px] font-medium text-ink-dim">
              Recovery phrase length
            </span>
            <div className="flex gap-2">
              {[12, 24].map((count) => (
                <button
                  key={count}
                  type="button"
                  onClick={() => setWords(count)}
                  className={cx(
                    "flex-1 rounded-lg border px-3 py-2 text-sm transition-colors",
                    words === count
                      ? "border-brass/50 bg-brass-wash text-brass"
                      : "border-hairline text-ink-dim hover:border-hairline-bright",
                  )}
                >
                  {count} words
                </button>
              ))}
            </div>
            <p className="text-xs text-ink-faint">
              24 words carries 256 bits of entropy. 12 carries 128 — still far
              beyond brute force.
            </p>
          </div>
          )}

          <Input
            label="Vault password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={10}
            error={
              password.length > 0 && password.length < 10
                ? "At least 10 characters."
                : null
            }
            hint={
              password.length >= 10
                ? "Encrypts the recovery phrase on disk. Separate from your sign-in password."
                : "At least 10 characters. Encrypts the recovery phrase on disk."
            }
          />
          <Input
            label="Confirm vault password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            error={
              confirm && password !== confirm ? "Passwords do not match." : null
            }
          />

          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-hairline p-3">
            <input
              type="checkbox"
              checked={usePassphrase}
              onChange={(e) => setUsePassphrase(e.target.checked)}
              className="mt-0.5 accent-[var(--color-brass)]"
            />
            <span className="text-[13px]">
              <span className="font-medium">Add a BIP-39 passphrase</span>
              <span className="mt-0.5 block text-ink-faint">
                A "25th word" that produces a completely different wallet. There
                is no way to detect a wrong passphrase — it simply opens a
                different, empty wallet. Lose it and the funds are gone even
                with the recovery phrase.
              </span>
            </span>
          </label>

          {usePassphrase && (
            <Input
              label="Passphrase"
              type="password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              autoComplete="off"
            />
          )}

          {error && <Callout tone="danger">{error}</Callout>}

          <div className="flex gap-2 pt-1">
            <Button full onClick={close}>
              Cancel
            </Button>
            <Button
              variant="primary"
              full
              loading={busy}
              disabled={
                !name ||
                password.length < 10 ||
                password !== confirm ||
                (mode === "restore" && !checked?.valid)
              }
              onClick={submit}
            >
              {mode === "restore" ? "Restore" : "Create"}
            </Button>
          </div>

          {/* Name the unmet condition. A disabled button that does not say why
              is indistinguishable from a broken one. */}
          {(!name ||
            password.length < 10 ||
            password !== confirm ||
            (mode === "restore" && !checked?.valid)) && (
            <p className="text-center text-xs text-ink-faint">
              {!name
                ? "Name the wallet to continue."
                : password.length < 10
                  ? "The vault password needs at least 10 characters."
                  : password !== confirm
                    ? "The two passwords must match."
                    : !allFilled
                      ? `Enter all ${entryLength} words.`
                      : "The phrase is not yet valid."}
            </p>
          )}
        </div>
      )}

      {step === "phrase" && (
        <div className="space-y-4">
          <Callout tone="danger" title="Write this down now, on paper">
            These {mnemonic.split(" ").length} words are the only way to restore
            this wallet. They will never be shown again. Anyone who reads them
            can take your money — do not photograph them, do not put them in a
            password manager you do not control, and do not type them into
            anything else.
          </Callout>

          <div className="relative">
            <ol
              className={cx(
                "grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg border border-hairline bg-abyss p-4 sm:grid-cols-3",
                !revealed && "blur-sm select-none",
              )}
            >
              {mnemonic.split(" ").map((word, index) => (
                <li key={index} className="flex items-baseline gap-2 text-sm">
                  <span className="num w-5 shrink-0 text-right text-xs text-ink-faint">
                    {index + 1}
                  </span>
                  <span className="font-mono">{word}</span>
                </li>
              ))}
            </ol>

            {!revealed && (
              <button
                onClick={() => setRevealed(true)}
                className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg text-sm font-medium text-brass"
              >
                <Eye size={16} />
                Tap to reveal
              </button>
            )}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={() => setRevealed((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs text-ink-dim hover:text-ink"
            >
              {revealed ? <EyeOff size={13} /> : <Eye size={13} />}
              {revealed ? "Hide" : "Reveal"}
            </button>
            {revealed && <CopyButton value={mnemonic} label="Copy phrase" />}
          </div>

          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-warn/30 bg-warn/8 p-3">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 accent-[var(--color-warn)]"
            />
            <span className="text-[13px] text-ink">
              I have written down my recovery phrase and stored it somewhere
              safe offline.
            </span>
          </label>

          <Button
            variant="primary"
            full
            size="lg"
            disabled={!acknowledged}
            onClick={() => setStep("done")}
          >
            I have saved it
          </Button>
        </div>
      )}

      {step === "done" && (
        <div className="space-y-4 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-gain/30 bg-gain/10">
            <Check className="text-gain" size={22} />
          </div>
          <div>
            <p className="font-medium">
              {mode === "restore" ? "Wallet restored" : "Wallet ready"}
            </p>
            <p className="mt-1 text-sm text-ink-faint">
              Addresses have been derived across your selected chains. Press
              Sync to check for balances.
              {mode === "restore" &&
                " A restored wallet only shows funds once the chains have been scanned."}
            </p>
          </div>
          {mode === "create" && (
            <Callout tone="warn">
              <span className="inline-flex items-center gap-1.5">
                <AlertTriangle size={13} />
                Test your backup before sending real funds: restore the phrase
                into a fresh wallet and confirm the first address matches.
              </span>
            </Callout>
          )}
          {mode === "restore" && (
            <Callout tone="info">
              <span className="inline-flex items-center gap-1.5">
                <AlertTriangle size={13} />
                Nothing showing? Check whether the original wallet used a BIP-39
                passphrase — without it you are looking at a different, empty
                wallet.
              </span>
            </Callout>
          )}
          <Button variant="primary" full onClick={close}>
            Done
          </Button>
        </div>
      )}
    </Modal>
  );
}
